import { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { journeyApi } from '../../api/journeyApi';
import { travelMediaApi } from '../../api/travelMediaApi';
import { CREATE_JOURNEY_PRODUCT_CHROME as chrome } from '../journey-workspace/journeyWorkspacePresentation';
import {
  confirmDiscardUnsavedChanges,
  useBeforeUnloadWhen,
} from '../ux-closure/unsavedChangesGuard';
import { encodeDestinationSummary } from './myJourneysModel';
import {
  clearHandoffSession,
  parsePersonalMapJourneyHandoff,
  persistHandoffSession,
  readHandoffFromLocationState,
  readHandoffSession,
  wrapHandoffInLocationState,
} from '../personal-map/personalMapJourneyHandoff';
import './my-journeys.css';

function toLocalYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Figma 預設 3 天（含起迄）；僅初始建議，使用者可改。 */
function defaultThreeDayRange() {
  const start = new Date();
  start.setHours(12, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 2);
  return { startDate: toLocalYmd(start), endDate: toLocalYmd(end) };
}

function validateForm({ title, startDate, endDate }) {
  const errors = {};
  if (!String(title || '').trim()) errors.title = chrome.titleRequired;
  else if (String(title).trim().length > 100) errors.title = chrome.titleTooLong;
  if (!startDate) errors.startDate = chrome.startRequired;
  if (!endDate) errors.endDate = chrome.endRequired;
  if (startDate && endDate && endDate < startDate) {
    errors.endDate = chrome.endBeforeStart;
  }
  return errors;
}

export default function CreateJourneyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const titleId = useId();
  const destinationId = useId();
  const startId = useId();
  const endId = useId();
  const coverId = useId();
  const formErrorId = useId();
  const submitting = useRef(false);
  const coverInputRef = useRef(null);
  const coverPreviewUrlRef = useRef('');
  const coverOpGeneration = useRef(0);
  const mountedRef = useRef(true);
  const personalMapHandoffRef = useRef(
    readHandoffFromLocationState(location.state) || readHandoffSession(),
  );

  const defaultRangeRef = useRef(defaultThreeDayRange());
  const [title, setTitle] = useState('');
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState(defaultRangeRef.current.startDate);
  const [endDate, setEndDate] = useState(defaultRangeRef.current.endDate);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState('');
  const [coverError, setCoverError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  // After journey create succeeds, never call create again — only cover retry/skip.
  const [createdJourneyId, setCreatedJourneyId] = useState(null);
  const [createdRowVersion, setCreatedRowVersion] = useState(null);
  const [phase, setPhase] = useState('form'); // form | cover-failed

  const journeyAlreadyCreated = Boolean(createdJourneyId);
  const formDirty = Boolean(
    !journeyAlreadyCreated
    && (
      String(title || '').trim()
      || String(destination || '').trim()
      || coverFile
      || startDate !== defaultRangeRef.current.startDate
      || endDate !== defaultRangeRef.current.endDate
    ),
  );
  useBeforeUnloadWhen(formDirty || phase === 'cover-failed');

  useEffect(() => {
    mountedRef.current = true;
    const fromState = readHandoffFromLocationState(location.state);
    if (fromState) {
      personalMapHandoffRef.current = fromState;
      persistHandoffSession(fromState);
    } else if (!personalMapHandoffRef.current) {
      personalMapHandoffRef.current = readHandoffSession();
    }
    return () => {
      mountedRef.current = false;
      coverOpGeneration.current += 1;
      if (coverPreviewUrlRef.current) {
        URL.revokeObjectURL(coverPreviewUrlRef.current);
        coverPreviewUrlRef.current = '';
      }
    };
  }, [location.state]);

  function goToCreatedJourney(journeyId) {
    if (!mountedRef.current) return;
    const handoff = parsePersonalMapJourneyHandoff(personalMapHandoffRef.current);
    if (handoff) {
      const continued = {
        ...handoff,
        journeyId: String(journeyId),
        sourceContext: 'journey-create-continuation',
      };
      delete continued.journeyDayId;
      persistHandoffSession(continued);
      navigate(`/journeys/${journeyId}/planning`, {
        replace: true,
        state: wrapHandoffInLocationState(continued),
      });
      return;
    }
    clearHandoffSession();
    navigate(`/journeys/${journeyId}`, { replace: true });
  }

  function replaceCoverPreview(nextUrl) {
    if (coverPreviewUrlRef.current) URL.revokeObjectURL(coverPreviewUrlRef.current);
    coverPreviewUrlRef.current = nextUrl || '';
    setCoverPreviewUrl(nextUrl || '');
  }

  function onCoverPick(event) {
    const file = event.target.files?.[0] || null;
    setCoverError('');
    if (!file) {
      setCoverFile(null);
      replaceCoverPreview('');
      return;
    }
    if (!String(file.type || '').startsWith('image/')) {
      setCoverFile(null);
      replaceCoverPreview('');
      setCoverError(chrome.coverInvalidType);
      return;
    }
    setCoverFile(file);
    replaceCoverPreview(URL.createObjectURL(file));
  }

  function clearCover() {
    setCoverError('');
    setCoverFile(null);
    replaceCoverPreview('');
    if (coverInputRef.current) coverInputRef.current.value = '';
  }

  async function uploadAndPatchCover(journeyId, rowVersion, file) {
    const token = ++coverOpGeneration.current;
    const coverFileReference = await travelMediaApi.uploadImage(file);
    if (token !== coverOpGeneration.current || !mountedRef.current) {
      return { cancelled: true };
    }
    await journeyApi.updateBasicInformation(journeyId, {
      coverFileReference,
      expectedRowVersion: rowVersion,
    });
    if (token !== coverOpGeneration.current || !mountedRef.current) {
      return { cancelled: true };
    }
    return { cancelled: false };
  }

  async function onSubmit(event) {
    event.preventDefault();
    if (submitting.current) return;
    // Journey already created — never create again from this form.
    if (createdJourneyId) return;

    const nextErrors = validateForm({ title, startDate, endDate });
    setFieldErrors(nextErrors);
    setFormError('');
    setCoverError('');
    if (Object.keys(nextErrors).length) return;

    submitting.current = true;
    setBusy(true);
    try {
      const body = {
        title: title.trim(),
        startDate,
        endDate,
        destinationSummaryJson: encodeDestinationSummary(destination),
      };
      const created = await journeyApi.create(body);
      if (!mountedRef.current) return;
      const journeyId = created?.journeyId == null ? '' : String(created.journeyId);
      if (!/^[1-9]\d*$/.test(journeyId)) throw new Error(chrome.createFailed);

      const rowVersion = created?.rowVersion ?? null;
      setCreatedJourneyId(journeyId);
      setCreatedRowVersion(rowVersion);

      if (!coverFile) {
        goToCreatedJourney(journeyId);
        return;
      }

      try {
        const result = await uploadAndPatchCover(journeyId, rowVersion, coverFile);
        if (result.cancelled || !mountedRef.current) return;
        goToCreatedJourney(journeyId);
      } catch {
        if (!mountedRef.current) return;
        setCoverError(chrome.coverUploadFailed);
        setPhase('cover-failed');
        submitting.current = false;
        setBusy(false);
      }
    } catch (cause) {
      if (!mountedRef.current) return;
      const code = cause?.code ?? cause?.raw?.code;
      const key = String(cause?.raw?.key || '');
      if (code === 92000 || key.includes('validation')) {
        setFormError(cause?.message || chrome.validationFailed);
      } else {
        setFormError(cause?.message || chrome.createFailed);
      }
      submitting.current = false;
      setBusy(false);
    }
  }

  async function retryCover() {
    if (!createdJourneyId || !coverFile || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setCoverError('');
    try {
      const result = await uploadAndPatchCover(createdJourneyId, createdRowVersion, coverFile);
      if (result.cancelled || !mountedRef.current) return;
      goToCreatedJourney(createdJourneyId);
    } catch {
      if (!mountedRef.current) return;
      setCoverError(chrome.coverUploadFailed);
      setPhase('cover-failed');
      submitting.current = false;
      setBusy(false);
    }
  }

  function skipCover() {
    if (!createdJourneyId) return;
    coverOpGeneration.current += 1;
    goToCreatedJourney(createdJourneyId);
  }

  const coverFailed = phase === 'cover-failed' && journeyAlreadyCreated;

  return (
    <main className="create-journey">
      <header className="create-journey__topbar">
        {!journeyAlreadyCreated ? (
          <button
            type="button"
            className="create-journey__close"
            data-testid="create-journey-back"
            aria-label={chrome.backAria}
            onClick={() => {
              if (!confirmDiscardUnsavedChanges(formDirty)) return;
              const handoff = parsePersonalMapJourneyHandoff(personalMapHandoffRef.current);
              const returnTo = handoff?.returnTo || '/journeys';
              clearHandoffSession();
              personalMapHandoffRef.current = null;
              navigate(returnTo);
            }}
          >
            ×
          </button>
        ) : (
          <span className="create-journey__close create-journey__close--spacer" aria-hidden="true" />
        )}
        <h1 className="create-journey__title">{chrome.title}</h1>
        <p className="create-journey__step" data-testid="create-journey-step" aria-hidden="true">
          {chrome.stepLabel}
        </p>
      </header>

      {/* Visual-only progress（C-WIZARD-01）：無 Backend wizard state */}
      <div
        className="create-journey__progress"
        data-testid="create-journey-progress"
        role="presentation"
        aria-hidden="true"
      >
        <span className="create-journey__progress-seg is-active" />
        <span className="create-journey__progress-seg" />
        <span className="create-journey__progress-seg" />
        <span className="create-journey__progress-seg" />
      </div>

      <form className="create-journey__form" onSubmit={onSubmit} noValidate>
        <section
          className="create-journey__cover"
          data-testid="create-journey-cover"
          aria-labelledby={`${coverId}-label`}
        >
          <p id={`${coverId}-label`} className="create-journey__cover-label visually-hidden">
            {chrome.coverSectionLabel}
          </p>
          <div
            className={
              `create-journey__cover-preview${coverPreviewUrl ? ' has-image' : ''}`
            }
            style={coverPreviewUrl ? { backgroundImage: `url(${coverPreviewUrl})` } : undefined}
          >
            {!coverPreviewUrl ? (
              <span className="create-journey__cover-empty">{chrome.coverEmptyHint}</span>
            ) : null}
            <div className="create-journey__cover-actions">
              <button
                type="button"
                className="create-journey__cover-pick"
                disabled={busy}
                onClick={() => coverInputRef.current?.click()}
                data-testid="create-journey-cover-pick"
              >
                {coverPreviewUrl ? chrome.coverChangeLabel : chrome.coverPickLabel}
              </button>
              {coverPreviewUrl && !journeyAlreadyCreated ? (
                <button
                  type="button"
                  className="create-journey__cover-clear"
                  disabled={busy}
                  onClick={clearCover}
                  data-testid="create-journey-cover-clear"
                >
                  {chrome.coverClearLabel}
                </button>
              ) : null}
            </div>
          </div>
          <input
            ref={coverInputRef}
            id={coverId}
            type="file"
            accept="image/*"
            hidden
            disabled={busy}
            onChange={onCoverPick}
            data-testid="create-journey-cover-input"
          />
          {coverError ? (
            <p className="create-journey__error" role="alert" data-testid="create-journey-cover-error">
              {coverError}
            </p>
          ) : null}
        </section>

        {coverFailed ? (
          <section className="create-journey__cover-recovery" data-testid="create-journey-cover-recovery">
            <p>行程已建立，但封面尚未設定成功。</p>
            <div className="create-journey__cover-recovery-actions">
              <button
                type="button"
                disabled={busy || !coverFile}
                onClick={retryCover}
                data-testid="create-journey-cover-retry"
              >
                {busy ? chrome.coverRetrying : chrome.coverRetryLabel}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={skipCover}
                data-testid="create-journey-cover-skip"
              >
                {chrome.coverSkipLabel}
              </button>
            </div>
          </section>
        ) : null}

        <fieldset disabled={journeyAlreadyCreated || busy} className="create-journey__fields">
          <label className="create-journey__field" htmlFor={titleId}>
            {chrome.fieldTitle}
            <input
              id={titleId}
              name="title"
              value={title}
              maxLength={100}
              autoComplete="off"
              aria-invalid={Boolean(fieldErrors.title)}
              aria-describedby={fieldErrors.title ? `${titleId}-error` : undefined}
              onChange={(event) => setTitle(event.target.value)}
            />
            {fieldErrors.title ? <span id={`${titleId}-error`} role="alert">{fieldErrors.title}</span> : null}
          </label>

          <div className="create-journey__dates" role="group" aria-labelledby="create-journey-dates-label">
            <p id="create-journey-dates-label" className="create-journey__dates-label">{chrome.fieldDates}</p>
            <div className="create-journey__dates-row">
              <label className="create-journey__field create-journey__field--date" htmlFor={startId}>
                <span className="visually-hidden">{chrome.fieldStart}</span>
                <input
                  id={startId}
                  name="startDate"
                  type="date"
                  value={startDate}
                  aria-invalid={Boolean(fieldErrors.startDate)}
                  aria-describedby={fieldErrors.startDate ? `${startId}-error` : 'create-journey-date-hint'}
                  onChange={(event) => setStartDate(event.target.value)}
                />
                {fieldErrors.startDate ? <span id={`${startId}-error`} role="alert">{fieldErrors.startDate}</span> : null}
              </label>
              <span className="create-journey__dates-sep" aria-hidden="true">{chrome.dateRangeSeparator}</span>
              <label className="create-journey__field create-journey__field--date" htmlFor={endId}>
                <span className="visually-hidden">{chrome.fieldEnd}</span>
                <input
                  id={endId}
                  name="endDate"
                  type="date"
                  value={endDate}
                  aria-invalid={Boolean(fieldErrors.endDate)}
                  aria-describedby={fieldErrors.endDate ? `${endId}-error` : 'create-journey-date-hint'}
                  onChange={(event) => setEndDate(event.target.value)}
                />
                {fieldErrors.endDate ? <span id={`${endId}-error`} role="alert">{fieldErrors.endDate}</span> : null}
              </label>
            </div>
          </div>
          <p id="create-journey-date-hint" className="create-journey__date-hint">{chrome.lead}</p>

          <label className="create-journey__field" htmlFor={destinationId}>
            {chrome.fieldDestination}
            <input
              id={destinationId}
              name="destination"
              value={destination}
              autoComplete="off"
              onChange={(event) => setDestination(event.target.value)}
            />
          </label>
        </fieldset>

        {formError ? <p id={formErrorId} className="create-journey__error" role="alert">{formError}</p> : null}

        {!journeyAlreadyCreated ? (
          <div className="create-journey__footer">
            <button type="submit" className="create-journey__submit" disabled={busy}>
              {busy ? chrome.submitting : chrome.submit}
            </button>
          </div>
        ) : null}
      </form>
    </main>
  );
}
